import { enterpriseLastMileDelivery } from '../../utils/content-settings';
import { updatePriceSettingsFromVisibilityOption } from '../../utils/pricing';
import { checkContentSettingsForChangesOnSave } from '../../utils/password';
import { isValidUrl } from '../../utils/validate';

const MAX_ZIP_CODE_FIELD_LENGTH = 60000; // gives you >8.5k zip codes in your list (more than CA + TX + PA)

export default function ModalCreateChannelController(
  $uibModalInstance,
  $rootScope,
  ConfirmDialog,
  Channel,
  Podcast,
  toaster,
  channel,
  parentVM,
  minTicketPrice,
  defaultTicketPrice
) {
  'ngInject';

  this.showError = false;
  this.FeatureFlags = $rootScope.account.feature_flags;
  this.ExtraFeatures = $rootScope.account.extra_features;
  this.MAX_ZIP_CODE_FIELD_LENGTH = MAX_ZIP_CODE_FIELD_LENGTH;

  if (this.FeatureFlags.no_public) {
    // If we don't allow public broadcasts (usually set for flex sites),
    // we need to demand that broadcasts accessed through a channel fall
    // through to the individual event configuration.
    this.showTicketedChannelSupport = true;
    this.showVisibility = false;
    this.visibility = 'public';
  } else if (this.FeatureFlags.no_ticketed) {
    // If we don't allow ticketed broadcasts, we don't show the visibility, but we also don't need
    // to show the support block about ticketed channels.
    this.showTicketedChannelSupport = false;
    this.showVisibility = false;
    this.visibility = 'public';
  } else {
    this.showTicketedChannelSupport = false;
    this.showVisibility = true;
    this.minTicketPrice = minTicketPrice;

    if (channel.free_variant == 'none') {
      this.visibility = 'ticketed';
    } else if (channel.free_variant == 'low') {
      this.visibility = 'flex';
    } else {
      this.visibility = 'public';
    }
  }

  this.limits = $rootScope.ApiConfig.modelFieldLimits('channel', 'maximum');

  this.channel = angular.copy(channel);
  this.channel.content_settings = this.channel.content_settings || {};

  // Load content languages (and account default, if set).
  let langs = angular.copy($rootScope.ApiConfig.content_languages || []);
  const accountDefaultLanguage = $rootScope.account.content_settings.language;
  if (accountDefaultLanguage) {
    const accountDefaultLanguageLabel = (
      _.findWhere($rootScope.ApiConfig.content_languages || [], {
        value: accountDefaultLanguage,
      }) || {}
    ).label;
    if (accountDefaultLanguageLabel) {
      langs.unshift({ value: '', label: `Use account default (${accountDefaultLanguageLabel})` });
    }
  }
  this.contentLanguages = langs;
  if (!this.channel.content_settings.language) {
    this.channel.content_settings.language = '';
  }

  if (this.FeatureFlags.podcast) {
    // Load podcast categories
    Podcast.getCategories(
      (result) => {
        let podcastCategories = [];
        _.each(result, (categories, code) => {
          const category = categories[0];
          const subcategory = categories[1];
          if (category && subcategory) {
            podcastCategories.push({ value: code, label: `${category} — ${subcategory}` });
          } else if (category) {
            podcastCategories.push({ value: code, label: category });
          }
        });
        this.podcastCategories = podcastCategories;
      },
      (error) => {
        console.warn(error);
      }
    );

    // Set default placeholders
    const accountDisplayName = $rootScope.account.content_settings.display_name;
    this.defaultPodcastAuthor = accountDisplayName || $rootScope.account.name;
  }

  // Support for content settings editor
  this.allowContentSettingsEdit = (key) => {
    var isSupportedDataType = typeof key == 'string';
    return !_.includes(PROTECTED_CONTENT_SETTINGS_KEYS, key) && isSupportedDataType;
  };
  this.showContentSettings = _.filter(
    _.keys(this.channel.content_settings),
    this.allowContentSettingsEdit
  ).length;
  this.addContentSettings = () => {
    var key = prompt('Enter the field key:');
    if (!key || !key.trim()) {
      return;
    } else if (typeof this.channel.content_settings[key] != 'undefined') {
      toaster.pop('warning', `Unable to add new key: ${key} already exists.`);
    } else {
      this.channel.content_settings[key] = '';
    }
  };
  this.updateContentSettings = (key, value) => {
    this.channel.content_settings[key] = value;
  };
  this.deleteContentSettings = (key) => {
    delete this.channel.content_settings[key];
  };

  if (!this.channel.ticket_price) {
    this.channel.ticket_price = defaultTicketPrice;
  }

  // Podcast-related functions
  const checkAndSetPodcastOwner = () => {
    const podcastSettings = this.channel.content_settings.podcast || {};
    if (!podcastSettings.owner_email || !podcastSettings.owner_name) {
      podcastSettings.owner_email = ($rootScope.user || {}).email;
      podcastSettings.owner_name = ($rootScope.user || {}).name;
    }
  };
  const sanitizePodcastSettings = () => {
    if (!this.FeatureFlags.podcast) {
      delete this.channel.content_settings.podcast;
      return;
    }

    const podcastSettings = this.channel.content_settings.podcast || {};
    if (!podcastSettings.audio && !podcastSettings.video) {
      delete this.channel.content_settings.podcast;
      return;
    }
  };
  this.showPodcastSettings = () => {
    const podcastSettings = this.channel.content_settings.podcast || {};
    return podcastSettings.audio || podcastSettings.video;
  };
  this.toggleEnablePodcast = (which) => {
    const podcastSettings = this.channel.content_settings.podcast || {};
    if (podcastSettings[which]) {
      delete this.channel.content_settings.podcast[which];
    } else {
      if (!this.channel.content_settings.podcast) this.channel.content_settings.podcast = {};
      this.channel.content_settings.podcast[which] = true;
      checkAndSetPodcastOwner();
    }
  };

  this.enterpriseLastMileDelivery = () => {
    return enterpriseLastMileDelivery(this.channel);
  };

  this.cancel = () => $uibModalInstance.dismiss('cancel');

  this.delete = () => {
    let modalOptions = {
      closeButtonText: 'Cancel',
      actionButtonText: 'Delete Channel',
      actionButtonClass: 'danger',
      headerText: 'Please Confirm',
      bodyText:
        'Are you sure you want to delete this channel? Any links or embed widgets you have pointing to it will become invalid.',
    };

    ConfirmDialog.show(modalOptions).then(() => {
      Channel.delete({ id: this.channel.id }, () => {
        parentVM.selectedChannel = null;
        parentVM.reload();
        this.cancel();
      });
    });
  };

  let validate = () => {
    if (!this.channel.name || !this.visibility) {
      return false;
    }
    if (this.visibility == 'ticketed' || this.visibility == 'flex') {
      if (!this.channel.ticket_price || this.channel.ticket_price < minTicketPrice) {
        return false;
      }
    }

    // Check for valid video portal configuration
    this.showInvalidPasswordForVideoPortal = false;
    this.showInvalidVisibilityForVideoPortal = false;
    if (this.ExtraFeatures.video_portal) {
      if (this.visibility != 'public' && this.channel.is_private === false)
        this.showInvalidVisibilityForVideoPortal = true;

      if (this.channel.is_private === false && this.channel.content_settings.cryptblock)
        this.showInvalidPasswordForVideoPortal = true;
    }

    // Look at possible password changes.
    checkContentSettingsForChangesOnSave(this.channel.content_settings);

    // Validate website URL
    this.showWebsiteUrlError = false;
    if (
      this.channel.content_settings.website &&
      !isValidUrl(this.channel.content_settings.website)
    ) {
      this.showWebsiteUrlError = true;
    }
    return (
      !this.showWebsiteUrlError &&
      !this.showInvalidVisibilityForVideoPortal &&
      !this.showInvalidPasswordForVideoPortal
    );
  };

  this.uploadPreviewImage = (file) => {
    file.upload().then((result) => {
      this.channel.content_settings = this.channel.content_settings || {};
      this.channel.content_settings.preview = result.view_url;
    });
  };

  this.uploadPodcastImage = (file) => {
    file.upload().then((result) => {
      console.log('Podcast image uploaded', result);
      this.channel.content_settings = this.channel.content_settings || {};
      this.channel.content_settings.podcast = this.channel.content_settings.podcast || {};
      this.channel.content_settings.podcast.image = result.view_url;
    });
  };

  this.update = () => {
    if (!validate()) {
      this.showError = true;
      return;
    }
    this.showError = false;
    if (!this.channel.content_settings.language) {
      delete this.channel.content_settings.language;
    }
    sanitizePodcastSettings();
    sanitizeGeoblockSettings();

    // Third param tells our price settings it's a channel so it won't override is_private
    this.channel = updatePriceSettingsFromVisibilityOption(this.channel, this.visibility, true);
    Channel.update(
      { id: channel.id },
      this.channel,
      () => {
        parentVM.reload();
        this.cancel();
      },
      (error) => {
        alert('Unable to create channel. Please refresh the page and try again.');
        console.warn(error);
      }
    );
  };

  this.create = () => {
    if (!validate()) {
      this.showError = true;
      return;
    }
    this.showError = false;
    if (!this.channel.content_settings.language) {
      delete this.channel.content_settings.language;
    }
    sanitizePodcastSettings();
    sanitizeGeoblockSettings();
    this.channel = updatePriceSettingsFromVisibilityOption(this.channel, this.visibility, true);
    Channel.create(
      this.channel,
      (c) => {
        parentVM.selectedChannel = c;
        parentVM.reload();
        this.cancel();
      },
      (error) => {
        alert('Unable to create channel. Please refresh the page and try again.');
        console.warn(error);
      }
    );
  };

  this.getZipCodeListLength = () => {
    return ((this.channel.content_settings.geoblock || {}).zip || '').length;
  };

  const sanitizeGeoblockSettings = () => {
    if (this.getZipCodeListLength() > MAX_ZIP_CODE_FIELD_LENGTH) {
      this.channel.content_settings.geoblock.zip =
        this.channel.content_settings.geoblock.zip.substr(0, MAX_ZIP_CODE_FIELD_LENGTH);
    }
  };
}



// WEBPACK FOOTER //
// ./app/pages/channels/modal-create-channel-controller.js